import useSWR, { Fetcher } from 'swr';
import { Invite } from '@prisma/client';
import { addInvite, deleteInvite, updateInvite } from '@/lib/api/invite';
import { InviteCreateInput, InviteUpdateInput } from '@/models/schema';

const fetcher: Fetcher<Invite[], string> = (url: string) => fetch(url).then((res) => res.json());

export function useInvites({ scheduleId }: { scheduleId: string }) {
  const { data, error, isLoading, mutate } = useSWR(
    `/api/schedules/${scheduleId}/invites`,
    fetcher,
    {
      revalidateIfStale: false,
    },
  );

  const add = async (input: InviteCreateInput) => {
    await addInvite(input);
  };

  const remove = async (id: string) => {
    return await mutate<Invite[]>(
      async (invites) => {
        await deleteInvite(scheduleId, id);

        const newInvites = invites?.filter((t) => t.id !== id);
        return [...(newInvites ?? [])];
      },
      {
        optimisticData: (invites) => invites?.filter((t) => t.id !== id) ?? [],
        revalidate: false,
        throwOnError: true,
      },
    );
  };

  const update = async (id: string, role: InviteUpdateInput['role']) => {
    return await mutate<Invite[]>(
      async (invites) => {
        const updatedInvite = await updateInvite(scheduleId, id, role);

        const newInvites = invites?.map((invite) => (invite.id === id ? updatedInvite : invite));
        return [...(newInvites ?? [])];
      },
      {
        optimisticData: (invites) =>
          invites?.map((invite) => (invite.id === id ? { ...invite, role } : invite)) ?? [],
        revalidate: false,
        throwOnError: true,
      },
    );
  };

  return {
    invites: data ?? [],
    isLoading,
    isError: error,
    mutate,
    add,
    remove,
    update,
  };
}
